import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, Trash2, Plus, X } from 'lucide-react';
import {
  AcademicClass,
  AcademicClassesApi,
  ClassSubjectRow,
  ClassSubjectType,
  ElectiveGroup,
  SubjectsApi,
} from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<AcademicClass>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

/** One entry per distinct subject assigned to the class, split Compulsory/Elective — dedupes ClassSubjectRow's per-section fan-out (see docs/DEVIATIONS.md). */
function dedupeBySubject(rows: ClassSubjectRow[]): ClassSubjectRow[] {
  const seen = new Map<string, ClassSubjectRow>();
  for (const r of rows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, r);
  return [...seen.values()];
}

function SubjectChips({ classId, type }: { classId: string; type: ClassSubjectType }) {
  const { data } = useQuery({
    queryKey: ['academic-classes', classId, 'subjects'],
    queryFn: () => AcademicClassesApi.subjects(classId),
  });
  const subjects = dedupeBySubject(data ?? []).filter((s) => s.classSubjectType === type);
  if (!subjects.length) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <div className="flex max-w-xs flex-wrap gap-1">
      {subjects.map((s) => (
        <span key={s.subjectId} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
          {s.subjectName}
        </span>
      ))}
    </div>
  );
}

/** One elective group's subjects (removable) + its "Total Selectable Subjects" + an add-subject picker. */
function ElectiveGroupCard({
  classId,
  group,
  availableSubjects,
  onRemoveGroup,
}: {
  classId: string;
  group: ElectiveGroup;
  availableSubjects: { id: string; name: string }[];
  onRemoveGroup: () => void;
}) {
  const qc = useQueryClient();
  const [totalSelectable, setTotalSelectable] = useState(String(group.totalSelectableSubjects));
  const [addSubjectId, setAddSubjectId] = useState('');

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['academic-classes', classId, 'elective-groups'] });
    qc.invalidateQueries({ queryKey: ['academic-classes', classId, 'subjects'] });
  }

  const updateTotal = useMutation({
    mutationFn: (value: number) => AcademicClassesApi.updateElectiveGroup(classId, group.id, value),
    onSuccess: invalidate,
  });
  const removeSubject = useMutation({
    mutationFn: (classSubjectId: string) => AcademicClassesApi.removeSubject(classId, classSubjectId),
    onSuccess: invalidate,
  });
  const addSubject = useMutation({
    mutationFn: () => AcademicClassesApi.addElectiveGroupSubject(classId, group.id, addSubjectId),
    onSuccess: () => {
      setAddSubjectId('');
      invalidate();
    },
  });

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Group</span>
        <button
          type="button"
          title="Remove Group"
          className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
          onClick={onRemoveGroup}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-2 flex flex-wrap gap-1.5">
        {group.subjects.map((s) => (
          <span key={s.classSubjectId} className="inline-flex items-center gap-1 rounded bg-amber-50 py-0.5 pl-2 pr-1 text-xs text-amber-800">
            {s.subjectName}
            <button
              type="button"
              title="Remove"
              className="rounded p-0.5 hover:bg-red-100 hover:text-red-600"
              onClick={() => removeSubject.mutate(s.classSubjectId)}
              disabled={removeSubject.isPending}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      {removeSubject.error && <p className="mb-2 text-xs text-red-600">{errMsg(removeSubject.error)}</p>}

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select className="!w-44 !py-1 text-sm" value={addSubjectId} onChange={(e) => setAddSubjectId(e.target.value)}>
          <option value="">Add subject to group…</option>
          {availableSubjects.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <button
          className="btn-secondary flex items-center gap-1 !py-1 text-xs"
          disabled={!addSubjectId || addSubject.isPending}
          onClick={() => addSubject.mutate()}
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
      {addSubject.error && <p className="mb-2 text-xs text-red-600">{errMsg(addSubject.error)}</p>}

      <label className="flex items-center gap-2 text-xs text-slate-600">
        Total Selectable Subjects
        <Input
          type="number"
          min={1}
          max={group.subjects.length}
          className="!w-16 !py-1 text-sm"
          value={totalSelectable}
          onChange={(e) => setTotalSelectable(e.target.value)}
          onBlur={() => {
            const n = Number(totalSelectable);
            if (n && n !== group.totalSelectableSubjects) updateTotal.mutate(n);
          }}
        />
      </label>
      {updateTotal.error && <p className="mt-1 text-xs text-red-600">{errMsg(updateTotal.error)}</p>}
    </div>
  );
}

/** The "+ Add Elective Group" creation form — at least 2 subject slots (matching the old system's own minimum), a selectable-count, Create. */
function CreateElectiveGroupForm({
  classId,
  availableSubjects,
  onDone,
}: {
  classId: string;
  availableSubjects: { id: string; name: string }[];
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [slots, setSlots] = useState<string[]>(['', '']);
  const [totalSelectable, setTotalSelectable] = useState('1');

  const create = useMutation({
    mutationFn: () =>
      AcademicClassesApi.createElectiveGroup(classId, {
        subjectIds: slots.filter(Boolean),
        totalSelectableSubjects: Number(totalSelectable),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['academic-classes', classId, 'elective-groups'] });
      qc.invalidateQueries({ queryKey: ['academic-classes', classId, 'subjects'] });
      onDone();
    },
  });

  const filledCount = slots.filter(Boolean).length;

  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-3">
      {slots.map((value, i) => (
        <div key={i} className="mb-2 flex items-center gap-2">
          {i > 0 && <span className="text-xs text-slate-400">OR</span>}
          <Select
            className="!w-48 !py-1 text-sm"
            value={value}
            onChange={(e) => setSlots((s) => s.map((v, idx) => (idx === i ? e.target.value : v)))}
          >
            <option value="">Select Subject</option>
            {availableSubjects
              .filter((s) => value === s.id || !slots.includes(s.id))
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </Select>
          {slots.length > 2 && (
            <button
              type="button"
              className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setSlots((s) => s.filter((_, idx) => idx !== i))}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="btn-secondary mb-3 flex items-center gap-1 !py-1 text-xs"
        onClick={() => setSlots((s) => [...s, ''])}
      >
        <Plus className="h-3.5 w-3.5" /> Add subject option
      </button>

      <Field label="Total Selectable Subjects" required className="mb-3 max-w-[10rem]">
        <Input
          type="number"
          min={1}
          max={filledCount || undefined}
          value={totalSelectable}
          onChange={(e) => setTotalSelectable(e.target.value)}
        />
      </Field>

      {create.error && <p className="mb-2 text-xs text-red-600">{errMsg(create.error)}</p>}
      <div className="flex gap-2">
        <button className="btn-secondary !py-1 text-xs" onClick={onDone}>
          Cancel
        </button>
        <button
          className="btn-primary !py-1 text-xs"
          disabled={filledCount < 2 || !totalSelectable || Number(totalSelectable) > filledCount || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create Group'}
        </button>
      </div>
    </div>
  );
}

/** Add/remove subjects for one class, shown inside the manage modal. */
function ClassSubjectManager({ classId }: { classId: string }) {
  const qc = useQueryClient();
  const [subjectId, setSubjectId] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [removingGroup, setRemovingGroup] = useState<ElectiveGroup | null>(null);

  const { data: assigned } = useQuery({
    queryKey: ['academic-classes', classId, 'subjects'],
    queryFn: () => AcademicClassesApi.subjects(classId),
  });
  const { data: groups } = useQuery({
    queryKey: ['academic-classes', classId, 'elective-groups'],
    queryFn: () => AcademicClassesApi.listElectiveGroups(classId),
  });
  const { data: allSubjectsPage } = useQuery({
    queryKey: ['subjects', 'all-for-assign-picker'],
    // 100 is the backend's own max (PaginationQueryDto's @Max(100)) —
    // requesting more 400s the whole query silently, which is exactly
    // what made the "Add Subject" dropdown show zero options. See
    // docs/DEVIATIONS.md.
    queryFn: () => SubjectsApi.list({ limit: 100 }),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['academic-classes', classId, 'subjects'] });
    qc.invalidateQueries({ queryKey: ['academic-classes', classId, 'elective-groups'] });
  }

  const allAssigned = dedupeBySubject(assigned ?? []);
  const coreSubjects = allAssigned.filter((s) => s.classSubjectType === 'Compulsory');
  // Every currently-assigned subject (Compulsory OR already in an elective
  // group) is unavailable for both the Core "Add" picker and every
  // elective picker — a subject can only be assigned to a class once,
  // matching the backend's own assertSubjectAvailableForClass() check.
  const assignedIds = new Set(allAssigned.map((s) => s.subjectId));
  const availableSubjects = (allSubjectsPage?.items ?? []).filter((s) => !assignedIds.has(s.id));

  const assignCore = useMutation({
    mutationFn: () => AcademicClassesApi.assignSubject(classId, { subjectId, type: 'Compulsory' }),
    onSuccess: () => {
      setSubjectId('');
      invalidate();
    },
  });
  const removeCore = useMutation({
    mutationFn: (classSubjectId: string) => AcademicClassesApi.removeSubject(classId, classSubjectId),
    onSuccess: invalidate,
  });
  const removeGroup = useMutation({
    mutationFn: (groupId: string) => AcademicClassesApi.removeElectiveGroup(classId, groupId),
    onSuccess: () => {
      setRemovingGroup(null);
      invalidate();
    },
  });

  return (
    <div className="space-y-5">
      <div>
        <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Core Subjects</h4>
        {coreSubjects.length === 0 ? (
          <p className="mb-3 text-xs text-slate-400">No core subjects assigned yet.</p>
        ) : (
          <ul className="mb-3 divide-y divide-slate-100">
            {coreSubjects.map((s) => (
              <li key={s.subjectId} className="flex items-center justify-between py-1.5">
                <span className="flex items-center gap-2 text-sm text-slate-700">
                  {s.subjectName}
                  <Badge tone="slate">Compulsory</Badge>
                </span>
                <button
                  type="button"
                  title="Remove"
                  className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                  disabled={removeCore.isPending}
                  onClick={() => removeCore.mutate(s.classSubjectId)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {removeCore.error && <p className="mb-2 text-xs text-red-600">{errMsg(removeCore.error)}</p>}

        <div className="flex flex-wrap items-center gap-2">
          <Select className="!w-48" value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
            <option value="">Select subject</option>
            {availableSubjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <button
            className="btn-secondary flex items-center gap-1 !py-1.5 text-xs"
            disabled={!subjectId || assignCore.isPending}
            onClick={() => assignCore.mutate()}
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
        {assignCore.error && <p className="mt-1 text-xs text-red-600">{errMsg(assignCore.error)}</p>}
      </div>

      <div>
        <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Elective Subjects{' '}
          <span className="font-normal normal-case text-slate-400">(students pick N of each group's subjects)</span>
        </h4>
        <div className="space-y-3">
          {(groups ?? []).map((g) => (
            <ElectiveGroupCard
              key={g.id}
              classId={classId}
              group={g}
              availableSubjects={availableSubjects}
              onRemoveGroup={() => setRemovingGroup(g)}
            />
          ))}
        </div>

        {creatingGroup ? (
          <div className="mt-3">
            <CreateElectiveGroupForm classId={classId} availableSubjects={availableSubjects} onDone={() => setCreatingGroup(false)} />
          </div>
        ) : (
          <button
            type="button"
            className="btn-secondary mt-3 flex items-center gap-1 text-xs"
            onClick={() => setCreatingGroup(true)}
          >
            <Plus className="h-3.5 w-3.5" /> Add Elective Group
          </button>
        )}
      </div>

      <ConfirmDialog
        open={!!removingGroup}
        onClose={() => setRemovingGroup(null)}
        onConfirm={() => removeGroup.mutate(removingGroup!.id)}
        title="Remove elective group?"
        message={
          removeGroup.error
            ? errMsg(removeGroup.error)
            : `This removes the whole group and all ${removingGroup?.subjects.length ?? 0} of its subjects. This cannot be undone.`
        }
        confirmText="Remove"
        destructive
        loading={removeGroup.isPending}
      />
    </div>
  );
}

/**
 * Writes to the real `class_subjects`/`elective_subject_groups` tables via
 * `/classes/:id/subjects` and `/classes/:id/elective-groups` — see
 * docs/DEVIATIONS.md. The old `/school/subjects` write path (a
 * disconnected single-classId entity) is disabled. Elective groups are a
 * build-fresh feature — 0 real rows exist anywhere in the legacy source
 * for either `elective_subject_groups` or `class_subjects.type='Elective'`
 * (confirmed directly against the live legacy database, not just the
 * migrated subset).
 */
export function ClassSubjectsPage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'name', defaultSortOrder: 'asc' });
  const [managingClass, setManagingClass] = useState<AcademicClass | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['academic-classes', 'for-subjects-page', dt.params],
    queryFn: () =>
      AcademicClassesApi.list({
        ...dt.params,
        sortBy: dt.params.sortBy as 'name' | 'medium' | undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.accessor('name', { header: 'Class' }),
    columnHelper.accessor('mediumName', { id: 'medium', header: 'Medium' }),
    columnHelper.display({
      id: 'core',
      header: 'Core Subjects',
      cell: (info) => <SubjectChips classId={info.row.original.id} type="Compulsory" />,
    }),
    columnHelper.display({
      id: 'elective',
      header: 'Elective Subjects',
      cell: (info) => <SubjectChips classId={info.row.original.id} type="Elective" />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (info) => (
        <button className="btn-secondary !py-1 text-xs" onClick={() => setManagingClass(info.row.original)}>
          Manage
        </button>
      ),
    }),
  ];

  return (
    <div>
      <PageHeader
        title="Class Subject"
        description="Assign Core (Compulsory) and Elective subjects to each class."
      />

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage={dt.params.search ? 'No classes match your search.' : 'No classes set up for this school yet.'}
        toolbar={
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={dt.searchInput}
              onChange={(e) => dt.setSearchInput(e.target.value)}
              placeholder="Search class name…"
              className="pl-9"
            />
          </div>
        }
      />

      <Modal
        open={!!managingClass}
        onClose={() => setManagingClass(null)}
        title={managingClass ? `Subjects — ${managingClass.name}` : 'Subjects'}
        size="lg"
      >
        {managingClass && <ClassSubjectManager classId={managingClass.id} />}
      </Modal>
    </div>
  );
}
